// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useUserByPublicIdQuery } from "@/api/hooks/use-users";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserActivityTab } from "@/features/iam/user-activity-tab";
import { UserAlertsTab } from "@/features/iam/user-alerts-tab";
import { UserInfoTab } from "@/features/iam/user-info-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

// Tabs that used to live here and now live on `/iam/users/$publicId/edit`. Old links and bookmarks
// carrying `?t=edit|permissions|bo-permissions` are forwarded rather than silently dropped to Info.
const MOVED_TABS: Record<string, string | undefined> = {
  edit: undefined, // General is the edit page's default tab, so it needs no `t`
  permissions: "permissions",
  "bo-permissions": "bo-permissions",
};

export const Route = createFileRoute("/_authenticated/iam/users/$publicId/")({
  beforeLoad: ({ params, search }) => {
    if (search.t && search.t in MOVED_TABS) {
      const movedTo = MOVED_TABS[search.t];
      throw redirect({
        to: "/iam/users/$publicId/edit",
        params,
        search: movedTo ? { t: movedTo } : {},
      });
    }
  },
  component: UserDetailPage,
});

function UserDetailPage() {
  const { publicId } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  // Already loaded by the layout, which only renders this page once the user is in hand.
  const { data } = useUserByPublicIdQuery(publicId);

  const canSeeAlerts = hasPermission(PAGE_PERMISSIONS.iamUsersAlerts);
  const tabAllowed: Record<string, boolean> = {
    info: true,
    alerts: canSeeAlerts,
    // Activity log is work-in-progress — debug-mode only.
    activity: debugMode,
  };
  const requestedTab = tab ?? "info";
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  function handleTabChange(newTab: string) {
    navigate({
      to: "/iam/users/$publicId",
      params: { publicId },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (!data) return null;

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
      <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
        <TabsList>
          <TabsIndicator />
          <TabsTrigger value="info">{t("tabs.info")}</TabsTrigger>
          {canSeeAlerts && <TabsTrigger value="alerts">{t("tabs.alerts")}</TabsTrigger>}
          {debugMode && <TabsTrigger value="activity">{t("tabs.activityLog")}</TabsTrigger>}
        </TabsList>
      </div>
      <TabsContent value="info" className="space-y-4">
        <UserInfoTab detail={data} />
      </TabsContent>
      {canSeeAlerts && (
        <TabsContent value="alerts" className="space-y-4">
          <UserAlertsTab publicId={publicId} initialFilters={activeTab === "alerts" ? f : undefined} />
        </TabsContent>
      )}
      {debugMode && (
        <TabsContent value="activity" className="space-y-4">
          <UserActivityTab publicId={publicId} initialFilters={activeTab === "activity" ? f : undefined} />
        </TabsContent>
      )}
    </Tabs>
  );
}
